import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n/I18nProvider";
import { useToast } from "@/components/ui/use-toast";
import { Mail, MessageSquare, Send } from "lucide-react";
import { useSettingsStore } from "@/data/settingsStore";
import { getApiUrl } from "@/utils/apiConfig";

interface Props {
  children: React.ReactNode;
}

export default function ContactFormDialog({ children }: Props) {
  const { lang } = useI18n();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  
  const contactEmail = useSettingsStore(state => state.getSetting('kaynin_contact_email', 'contact@kaynin.ma')) as string;
  
  const [captcha, setCaptcha] = useState({ a: 0, b: 0, result: 0, answer: "" });

  const generateCaptcha = () => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ a, b, result: a + b, answer: "" });
  };

  const previousPathRef = useRef("");

  useEffect(() => {
    if (isOpen) {
      previousPathRef.current = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', '/contact');
      generateCaptcha();
      setFormData({ name: "", email: "", subject: "", message: "" });
    } else if (previousPathRef.current) {
      if (window.location.pathname === '/contact') {
        window.history.replaceState(null, '', previousPathRef.current);
      }
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast({
        title: lang === 'ar' ? "خطأ" : "Erreur",
        description: lang === 'ar' ? "يرجى ملء جميع الحقول الإلزامية." : "Veuillez remplir tous les champs obligatoires.",
        variant: "destructive"
      });
      return;
    }

    if (parseInt(captcha.answer) !== captcha.result) {
      toast({
        title: lang === 'ar' ? "خطأ في التحقق" : "Erreur de vérification",
        description: lang === 'ar' ? "إجابة التحقق غير صحيحة." : "La réponse au captcha est incorrecte.",
        variant: "destructive"
      });
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${getApiUrl()}/contact.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      
      if (!response.ok) throw new Error(result.error || "Erreur serveur");

      toast({
        title: lang === 'ar' ? "نجاح" : "Succès",
        description: lang === 'ar' ? "تم إرسال رسالتك بنجاح." : "Votre message a été envoyé avec succès.",
      });
      
      setIsOpen(false);
    } catch (err: any) {
      toast({
        title: lang === 'ar' ? "خطأ" : "Erreur",
        description: err.message || (lang === 'ar' ? "حدث خطأ أثناء الإرسال." : "Une erreur est survenue lors de l'envoi."),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const t = {
    title: lang === 'ar' ? "اتصل بنا" : lang === 'zgh' ? "ⴰⵡⴰⴹ ⴰⴽⴷ ⵏⵖ" : "Nous contacter",
    desc: lang === 'ar' ? "املأ النموذج أدناه لإرسال رسالة إلينا." : "Remplissez le formulaire ci-dessous pour nous envoyer un message.",
    name: lang === 'ar' ? "الاسم الكامل" : "Nom complet",
    email: lang === 'ar' ? "البريد الإلكتروني" : "Email",
    subject: lang === 'ar' ? "الموضوع" : "Objet",
    message: lang === 'ar' ? "الرسالة" : "Message",
    captcha: lang === 'ar' ? "كم حاصل جمع" : "Combien font",
    send: lang === 'ar' ? "إرسال الرسالة" : "Envoyer le message",
    cancel: lang === 'ar' ? "إلغاء" : "Annuler"
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] w-[95vw] rounded-3xl p-0 shadow-2xl overflow-hidden border-none max-h-[90vh] flex flex-col">
        <div className="h-2 w-full bg-gradient-to-r from-primary via-accent to-primary shrink-0" />

        <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-border/50">
          <DialogTitle className="text-2xl font-display text-primary flex items-center gap-2">
            <Mail className="w-6 h-6" /> {t.title}
          </DialogTitle>
          <DialogDescription className="font-body text-muted-foreground mt-1">
            {t.desc}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          <form id="contact-form" className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t.name} *</label>
              <Input type="text" name="name" value={formData.name} onChange={handleChange} required className="rounded-xl" />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">{t.email} *</label>
              <Input type="email" name="email" value={formData.email} onChange={handleChange} required className="rounded-xl" />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t.subject} *</label>
              <Input type="text" name="subject" value={formData.subject} onChange={handleChange} required className="rounded-xl" />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t.message} *</label>
              <textarea name="message" rows={5} value={formData.message} onChange={handleChange} required className="w-full rounded-xl border border-input shadow-sm bg-background p-4 focus:ring-2 focus:ring-primary/20 resize-none font-body text-sm outline-none" />
            </div>

            <div className="space-y-2 p-4 bg-muted/30 rounded-xl border border-border/50">
              <label className="text-sm font-medium block mb-2">{t.captcha} {captcha.a} + {captcha.b} ? *</label>
              <Input 
                type="number" 
                value={captcha.answer} 
                onChange={(e) => setCaptcha({ ...captcha, answer: e.target.value })} 
                required 
                className="rounded-xl max-w-[150px]" 
                placeholder="?"
              />
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-border/50 bg-muted/20 shrink-0 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => setIsOpen(false)} disabled={isSubmitting} className="h-11 rounded-xl px-6">
            {t.cancel}
          </Button>
          <Button type="submit" form="contact-form" disabled={isSubmitting} className="h-11 rounded-xl px-8 font-bold bg-primary hover:bg-primary/90 text-white shadow-md hover:scale-105 transition-transform flex gap-2 disabled:opacity-50 disabled:hover:scale-100">
            <Send className="w-4 h-4" /> {isSubmitting ? (lang === 'ar' ? "جاري الإرسال..." : "Envoi...") : t.send}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
